/**
 * Self-healing prompt system.
 *
 * Tracks per-callsite success/failure rates and auto-adjusts prompts
 * by appending healing suffixes and adjusting temperature. This is the
 * TypeScript equivalent of the Rust PromptHealer.
 */

/** Failure categories for prompt healing decisions. */
export type FailureType = "json_parse" | "empty_response" | "truncation" | "structure_mismatch";

/** Per-callsite failure statistics. */
interface CallsiteStats {
  successes: number;
  jsonFailures: number;
  emptyFailures: number;
  truncationFailures: number;
  structureFailures: number;
}

/** Serialized form of the healer state. */
interface HealerState {
  stats: Record<string, CallsiteStats>;
}

const FAILURE_THRESHOLDS = {
  jsonParse: 0.2,
  truncation: 0.15,
  empty: 0.1,
  structure: 0.15,
} as const;
const MIN_OBSERVATIONS = 3;
const TEMPERATURE_SCALE_FACTOR = 0.7;
const TEMPERATURE_FLOOR = 0.1;

const STORAGE_KEY = "dreadful-depths-prompt-healer";

/** Create a fresh callsite stats object with all counters at zero. */
function emptyStats(): CallsiteStats {
  return {
    successes: 0,
    jsonFailures: 0,
    emptyFailures: 0,
    truncationFailures: 0,
    structureFailures: 0,
  };
}

/** Total observations for a callsite. */
function totalCalls(s: CallsiteStats): number {
  return (
    s.successes + s.jsonFailures + s.emptyFailures + s.truncationFailures + s.structureFailures
  );
}

/**
 * Self-healing prompt adjuster.
 *
 * Monitors per-callsite success/failure rates and generates prompt
 * suffixes that steer the LLM away from repeated failure patterns.
 * Also adjusts temperature downward when failure rates are high.
 */
export class PromptHealer {
  private stats: Map<string, CallsiteStats> = new Map();

  /** Record a successful LLM call. */
  recordSuccess(callsite: string): void {
    const s = this.getOrCreate(callsite);
    s.successes++;
  }

  /** Record a failed LLM call with a specific failure category. */
  recordFailure(callsite: string, type: FailureType): void {
    const s = this.getOrCreate(callsite);
    switch (type) {
      case "json_parse":
        s.jsonFailures++;
        break;
      case "empty_response":
        s.emptyFailures++;
        break;
      case "truncation":
        s.truncationFailures++;
        break;
      case "structure_mismatch":
        s.structureFailures++;
        break;
    }
  }

  /**
   * Get healing instructions to append to a prompt based on failure patterns.
   *
   * Examines the failure rate for each category at the given callsite and
   * returns additional instructions the LLM should follow to avoid
   * repeating past mistakes. Returns an empty string if no healing is needed.
   */
  healingSuffix(callsite: string): string {
    const s = this.stats.get(callsite);
    if (!s) return "";

    const total = totalCalls(s);
    if (total < MIN_OBSERVATIONS) return "";

    const parts: string[] = [];

    // JSON parse failures > 20%: add strict JSON formatting instructions
    if (s.jsonFailures / total > FAILURE_THRESHOLDS.jsonParse) {
      parts.push(
        "IMPORTANT: Return ONLY valid JSON. No markdown fences, no commentary, no trailing commas. " +
          "Use double quotes for all strings. Verify your JSON is parseable before responding.",
      );
    }

    // Truncation > 15%: add conciseness instructions
    if (s.truncationFailures / total > FAILURE_THRESHOLDS.truncation) {
      parts.push(
        "IMPORTANT: Keep your response concise. Finish all JSON structures completely. " +
          "Do not start structures you cannot finish within the token limit.",
      );
    }

    // Empty responses > 10%: add non-empty requirement
    if (s.emptyFailures / total > FAILURE_THRESHOLDS.empty) {
      parts.push(
        "IMPORTANT: You MUST provide a non-empty response. " +
          "An empty or whitespace-only response is not acceptable.",
      );
    }

    // Structure mismatches > 15%: add schema reminder
    if (s.structureFailures / total > FAILURE_THRESHOLDS.structure) {
      parts.push(
        "IMPORTANT: Follow the requested output schema exactly. " +
          "Include all required fields with the correct types. " +
          "Do not add extra fields or omit required ones.",
      );
    }

    if (parts.length === 0) return "";
    return "\n\n" + parts.join("\n");
  }

  /**
   * Get adjusted temperature based on failure rate.
   *
   * When the overall failure rate is high, lowers the temperature toward
   * a minimum of 0.1 to encourage more deterministic (and hopefully
   * more structurally correct) output.
   */
  adjustedTemperature(callsite: string, base: number): number {
    const s = this.stats.get(callsite);
    if (!s) return base;

    const total = totalCalls(s);
    if (total < MIN_OBSERVATIONS) return base;

    const failureRate = 1 - s.successes / total;
    // Scale temperature down proportionally to failure rate
    // At 100% failure: temperature = max(TEMPERATURE_FLOOR, base * (1 - TEMPERATURE_SCALE_FACTOR))
    // At 50% failure: temperature = max(TEMPERATURE_FLOOR, base * (1 - TEMPERATURE_SCALE_FACTOR/2))
    // At 0% failure: temperature = base
    const scale = 1 - failureRate * TEMPERATURE_SCALE_FACTOR;
    return Math.max(TEMPERATURE_FLOOR, base * scale);
  }

  /** Get success rate for a callsite (0-1). Returns 1 if no data. */
  successRate(callsite: string): number {
    const s = this.stats.get(callsite);
    if (!s) return 1;
    const total = totalCalls(s);
    if (total === 0) return 1;
    return s.successes / total;
  }

  /** Serialize to JSON for persistence. */
  serialize(): string {
    const state: HealerState = {
      stats: Object.fromEntries(this.stats),
    };
    return JSON.stringify(state);
  }

  /** Deserialize from JSON. */
  static deserialize(json: string): PromptHealer {
    const healer = new PromptHealer();
    try {
      const state = JSON.parse(json) as HealerState;
      if (state.stats && typeof state.stats === "object") {
        for (const [key, value] of Object.entries(state.stats)) {
          healer.stats.set(key, value);
        }
      }
    } catch {
      // Return fresh healer on invalid JSON
    }
    return healer;
  }

  /** Save to localStorage. */
  save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, this.serialize());
    } catch {
      // localStorage may not be available (SSR, tests)
    }
  }

  /** Load from localStorage. Returns a fresh healer if nothing stored. */
  static load(): PromptHealer {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return PromptHealer.deserialize(stored);
    } catch {
      // localStorage may not be available
    }
    return new PromptHealer();
  }

  /** Get or create stats for a callsite. */
  private getOrCreate(callsite: string): CallsiteStats {
    let s = this.stats.get(callsite);
    if (!s) {
      s = emptyStats();
      this.stats.set(callsite, s);
    }
    return s;
  }
}
